// ════════════════════════════════════════════════════════════════
// useEffectiveAccess — Single source of truth for a user's access state
// Wraps the get_effective_access(user_id) RPC.
// ════════════════════════════════════════════════════════════════

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface EffectiveAccessRole {
  role_id: string;
  role_code: string;
  role_name: string;
  scope_type: 'global' | 'organisation';
  scope_id: string | null;
  assigned_at: string;
}

export interface EffectiveAccessSubscription {
  subscription_id: string;
  plan_id: string;
  plan_code: string;
  plan_name: string;
  plan_type: string;
  billing_interval: string;
  status: string;
  started_at: string;
  renews_at: string | null;
  ends_at: string | null;
}

export interface EffectiveAccessEntitlement {
  code: string;
  name: string;
  category: string;
  value: unknown;
  source: 'manual' | 'plan' | 'beta';
  source_ref: string | null;
  starts_at: string | null;
  ends_at: string | null;
}

export interface EffectiveAccessBetaProgram {
  id: string;
  code: string;
  name: string;
  starts_at: string | null;
  ends_at: string | null;
  membership_starts_at: string | null;
  membership_ends_at: string | null;
}

export interface EffectiveAccess {
  user_id: string;
  account_status: string;
  verification_status: string;
  is_verified: boolean;
  access_blocked: boolean;
  primary_role: string;
  subscription: EffectiveAccessSubscription | null;
  roles: EffectiveAccessRole[];
  permissions: string[];
  entitlements: EffectiveAccessEntitlement[];
  beta_programs: EffectiveAccessBetaProgram[];
  computed_at: string;
}

// ────────────────────────────────────────────────────────────────
// Identity Gate (Schicht A)
// Deterministische Ableitung des Identity-Zustands vor jeder
// Permission-/Feature-/Content-Prüfung. Source of Truth für
// "darf der User die App überhaupt betreten?".
// ────────────────────────────────────────────────────────────────
export type IdentityGateStatus =
  | 'guest'           // not authenticated
  | 'suspended'       // account_status = suspended
  | 'rejected'        // account_status = rejected
  | 'pending'         // account_status not active (e.g. registered, invited)
  | 'unverified'      // active but verification_status = unverified
  | 'no_beta'         // active + verified, but no active beta program (closed beta gate)
  | 'ok';             // full access

export interface IdentityGate {
  status: IdentityGateStatus;
  /** True when status === 'ok' OR user is admin (admins bypass beta gate) */
  hasAppAccess: boolean;
  isAdmin: boolean;
  accountStatus: string;
  verificationStatus: string;
}

/** Pure derivation — exported for tests and non-React callers. */
export function deriveIdentityGate(
  access: EffectiveAccess | null,
  isAuthenticated: boolean,
): IdentityGate {
  const accountStatus = access?.account_status ?? 'registered';
  const verificationStatus = access?.verification_status ?? 'unverified';
  const isAdmin = !!access?.roles.some(
    (r) => r.role_code === 'admin' || r.role_code === 'super_admin',
  );

  if (!isAuthenticated || !access) {
    return { status: 'guest', hasAppAccess: false, isAdmin: false, accountStatus, verificationStatus };
  }
  // Admins bypass account/beta gating entirely.
  if (isAdmin) {
    return { status: 'ok', hasAppAccess: true, isAdmin: true, accountStatus, verificationStatus };
  }
  if (accountStatus === 'suspended') {
    return { status: 'suspended', hasAppAccess: false, isAdmin: false, accountStatus, verificationStatus };
  }
  if (accountStatus === 'rejected') {
    return { status: 'rejected', hasAppAccess: false, isAdmin: false, accountStatus, verificationStatus };
  }
  if (accountStatus !== 'active') {
    return { status: 'pending', hasAppAccess: false, isAdmin: false, accountStatus, verificationStatus };
  }
  if (verificationStatus === 'unverified') {
    return { status: 'unverified', hasAppAccess: false, isAdmin: false, accountStatus, verificationStatus };
  }
  if ((access.beta_programs ?? []).length === 0) {
    return { status: 'no_beta', hasAppAccess: false, isAdmin: false, accountStatus, verificationStatus };
  }
  return { status: 'ok', hasAppAccess: true, isAdmin: false, accountStatus, verificationStatus };
}

/**
 * Returns the complete effective access state for a user as a single,
 * deterministic payload computed server-side.
 *
 * Use this as the canonical source for access decisions instead of
 * stitching results together from useUserRoles / useRbac / useEntitlements
 * / useMemberSubscription / useBetaAccess.
 */
export function useEffectiveAccess(userId?: string) {
  const { user } = useAuth();
  const targetUserId = userId ?? user?.id ?? null;

  const query = useQuery({
    queryKey: ['effective-access', targetUserId],
    enabled: !!targetUserId,
    staleTime: 60_000,
    // A transient backend hiccup must never look like "no access".
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    queryFn: async (): Promise<EffectiveAccess | null> => {
      if (!targetUserId) return null;
      const { data, error } = await supabase.rpc('get_effective_access', {
        _user_id: targetUserId,
      });
      if (error) throw error;
      return data as unknown as EffectiveAccess;
    },
  });

  const access = query.data ?? null;

  return {
    access,
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    /** Identity gate derived from account/verification/beta — Schicht A */
    identityGate: deriveIdentityGate(access, !!user),
    // Convenience accessors
    hasPermission: (key: string) => !!access?.permissions.includes(key),
    hasRole: (code: string) => !!access?.roles.some((r) => r.role_code === code),
    hasEntitlement: (code: string) =>
      !!access?.entitlements.some((e) => e.code === code),
    isInBetaProgram: (code: string) =>
      !!access?.beta_programs.some((b) => b.code === code),
  };
}
